import React, { useEffect, useState } from "react";

import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";

import { Col, Row } from "react-bootstrap";

import BrandButton from "@/common/BrandButton/BrandButton";
import styles from "./PaginationRow.module.scss";

interface Props {
  onClickPrevious: () => void;
  handleOnClickNext: () => void;
  disableLeft: boolean;
  disableNext: boolean;
  page: number;
  /** Total pages when known; enables “of N” and clamping. */
  totalPages?: number;
  /** Jump to a 1-based page. When omitted, page input is read-only display. */
  onPageChange?: (page: number) => void;
  /** Optional richer label replacing the default Page control (legacy). */
  label?: string;
  disabled?: boolean;
}

const PaginationRow: React.FC<Props> = ({
  onClickPrevious,
  handleOnClickNext,
  disableNext,
  disableLeft,
  page,
  totalPages,
  onPageChange,
  label,
  disabled = false,
}) => {
  const [draft, setDraft] = useState(String(page));

  useEffect(() => {
    setDraft(String(page));
  }, [page]);

  const applyJump = () => {
    if (!onPageChange) return;
    const raw = Number.parseInt(draft, 10);
    if (!Number.isFinite(raw)) {
      setDraft(String(page));
      return;
    }
    let next = raw;
    if (next < 1) next = 1;
    if (typeof totalPages === "number" && totalPages >= 1 && next > totalPages) {
      next = totalPages;
    }
    setDraft(String(next));
    if (next !== page) onPageChange(next);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      applyJump();
    }
  };

  return (
    <Row>
      <Col className="d-flex justify-content-end align-items-center">
        <BrandButton
          className="m-0"
          onClick={onClickPrevious}
          disabled={disableLeft || disabled}
        >
          <ArrowLeftOutlined /> Prev
        </BrandButton>
        <div className={`mx-3 d-flex align-items-center ${styles.pageNumberText}`}>
          {label && !onPageChange ? (
            <span>{label}</span>
          ) : (
            <>
              <span className="me-1">Page</span>
              {onPageChange ? (
                <input
                  className={styles.pageInput}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  aria-label="Go to page"
                  title="Type a page number, then press Enter"
                  data-testid="page-jump-input"
                  value={draft}
                  disabled={disabled}
                  onChange={(e) => {
                    // Digits only — keeps the control looking like a page field
                    // without relying on browser number-spinner chrome.
                    const next = e.target.value.replace(/\D/g, "");
                    setDraft(next);
                  }}
                  onBlur={applyJump}
                  onKeyDown={onKeyDown}
                />
              ) : (
                <span>{page}</span>
              )}
              {typeof totalPages === "number" && totalPages > 0 ? (
                <span className="ms-1">of {totalPages.toLocaleString()}</span>
              ) : null}
            </>
          )}
        </div>
        <BrandButton
          className="m-0"
          onClick={handleOnClickNext}
          disabled={disableNext || disabled}
        >
          Next <ArrowRightOutlined />
        </BrandButton>
      </Col>
    </Row>
  );
};

export default PaginationRow;
